import { BusinessListing } from "@/types/directory";
import { MapPin, Star, Building2 } from "lucide-react";
import { getCurrentOpenStatus } from "@/lib/utils";
import { getSpecialtyLabel } from "@/lib/specialties";
import { useImageLoader } from "@/hooks/useImageLoader";
import {
  dateOnlyFromInstant,
  differenceInDateOnlyDays,
  formatDateOnlyForDisplay,
  isValidTimeZone,
  parseDateOnly,
  weekdayForDateOnly,
} from "@/lib/salon-time";

interface BusinessCardProps {
  business: BusinessListing;
}

// Title case a string (capitalize first letter of each word)
const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const BusinessCard = ({ business }: BusinessCardProps) => {
  // Get unique cities (deduplicated, case-insensitive, title-cased)
  const uniqueCities = business.locations
    ?.map((loc) => loc.city)
    .filter((city): city is string => Boolean(city))
    .reduce((acc, city) => {
      const lowerCity = city.toLowerCase();
      if (!acc.some((c) => c.toLowerCase() === lowerCity)) {
        acc.push(toTitleCase(city));
      }
      return acc;
    }, [] as string[]) || [];

  const displayedCities = uniqueCities.slice(0, 3);
  const hasMoreCities = uniqueCities.length > 3;

  // Validate images actually load
  const headerImageValid = useImageLoader(business.header_image_url);
  const logoValid = useImageLoader(business.logo_url);

  // Convert salon_hours to the format expected by getCurrentOpenStatus
  const formattedHours = business.salon_hours?.map((hour) => ({
    day: hour.day,
    openTime: hour.open_time,
    closeTime: hour.close_time,
    isClosed: hour.is_closed,
  })) || [];
  const scheduleTimezone = business.schedule_salon?.timezone;

  // Pass closed periods to getCurrentOpenStatus
  const { isOpen, nextOpenTime } = getCurrentOpenStatus(
    formattedHours,
    scheduleTimezone,
    business.closed_periods || []
  );

  // Format time from 24h to 12h format
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  // Get current day for comparison
  const getCurrentDay = () => {
    if (!isValidTimeZone(scheduleTimezone)) return "";
    const today = parseDateOnly(
      dateOnlyFromInstant(new Date(), scheduleTimezone),
    )!;
    return weekdayForDateOnly(today).toLowerCase();
  };

  // Format the "opens at" text
  const getOpensAtText = () => {
    if (!nextOpenTime || !isValidTimeZone(scheduleTimezone)) return null;
    const currentDay = getCurrentDay();
    const formattedTime = formatTime(nextOpenTime.time);
    
    // Check if the next open time is within 7 days
    const today = parseDateOnly(
      dateOnlyFromInstant(new Date(), scheduleTimezone),
    )!;
    const nextOpenDate = nextOpenTime.date || null;
    const daysDifference = nextOpenDate
      ? differenceInDateOnlyDays(nextOpenDate, today)
      : 0;

    // Check if it's actually today (same day name AND within 1 day)
    const isToday = nextOpenTime.day === currentDay && daysDifference <= 1;
    
    if (isToday) {
      return `Opens ${formattedTime}`;
    } else if (daysDifference <= 7) {
      // Within 7 days - show day name
      const dayName = nextOpenTime.day.charAt(0).toUpperCase() + nextOpenTime.day.slice(1);
      return `Opens ${dayName} ${formattedTime}`;
    } else {
      // Beyond 7 days - show date
      const dateStr = nextOpenDate
        ? formatDateOnlyForDisplay(nextOpenDate, {
            month: "short",
            day: "numeric",
          })
        : "";
      return `Opens ${dateStr} ${formattedTime}`;
    }
  };

  return (
    <a
      href={`/${business.booking_url_identifier}?source=marketplace`}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden group flex flex-col h-full border border-grey-100"
    >
      {/* Card Header */}
      <div className="h-36 relative">
        {headerImageValid ? (
          <>
            <img
              src={business.header_image_url!}
              alt={`${business.name}`}
              className="w-full h-full object-cover"
            />
            {/* Subtle gradient overlay for better text contrast if needed */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-green-25 to-green-75 flex items-center justify-center">
            {logoValid ? (
              <img
                src={business.logo_url!}
                alt={`${business.name} logo`}
                className="h-16 w-16 object-contain rounded-full bg-white p-1 shadow-sm"
              />
            ) : (
              <div className="h-16 w-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                <Building2 className="h-8 w-8 text-green-300" />
              </div>
            )}
          </div>
        )}
        
        {/* Logo overlay when header image exists */}
        {headerImageValid && logoValid && (
          <div className="absolute bottom-3 left-4">
            <img
              src={business.logo_url!}
              alt={`${business.name} logo`}
              className="h-10 w-10 object-contain rounded-full bg-white p-0.5 shadow-md border border-white"
            />
          </div>
        )}

        {/* Open/Closed Badge */}
        {business.salon_hours && business.salon_hours.length > 0 && (
          <div className="absolute top-3 right-3">
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                isOpen
                  ? "bg-green-500 text-white"
                  : "bg-grey-700 text-white"
              }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  isOpen ? "bg-white" : "bg-grey-400"
                }`}
              />
              {isOpen ? "Open" : getOpensAtText() || "Closed"}
            </div>
          </div>
        )}
      </div>

      {/* Card Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex-1">
          {/* Business Name */}
          <h3 className="text-lg font-semibold text-grey-900 mb-2 group-hover:text-green-300 transition-colors line-clamp-1">
            {toTitleCase(business.name)}
          </h3>

          {/* Locations as pills */}
          {displayedCities.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <MapPin className="h-4 w-4 text-grey-400 flex-shrink-0" />
              {displayedCities.map((city, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-600"
                >
                  {city}
                </span>
              ))}
              {hasMoreCities && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-grey-50 text-grey-400">
                  +{uniqueCities.length - 3}
                </span>
              )}
            </div>
          )}

          {/* About/Description */}
          {business.about && (
            <p className="text-sm text-grey-500 line-clamp-2 mb-3">
              {business.about}
            </p>
          )}

          {/* Specialties */}
          {business.specialties && business.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {business.specialties.slice(0, 3).map((specialty, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-25 text-green-300"
                >
                  {getSpecialtyLabel(specialty)}
                </span>
              ))}
              {business.specialties.length > 3 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-grey-50 text-grey-500">
                  +{business.specialties.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Reviews - at bottom */}
        {business.review_summary.count > 0 && business.review_summary.average && (
          <div className="mt-auto pt-3 border-t border-grey-100 flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-medium text-grey-700">
              {business.review_summary.average.toFixed(1)}
            </span>
            <span className="text-sm text-grey-400">
              ({business.review_summary.count} review
              {business.review_summary.count !== 1 ? "s" : ""})
            </span>
          </div>
        )}
      </div>
    </a>
  );
};

export default BusinessCard;
